"use client";

import React, {
  useCallback,
  useMemo,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import FocusTrap from "@mui/material/Unstable_TrapFocus";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";
import { useParams } from "next/navigation";
import { useThrottledCallback } from "use-debounce";

import { GlobalPathParams } from "~/app/[locale]/types";
import { BffComponentType } from "~/bff/ComponentsConfig";
import { ImageCommon } from "~/bff/types/ImageCommon";
import { SearchPanel } from "~/bff/types/SearchPanel";
import { AccountButtonDesktop } from "~/components/account-page/components/account-button-desktop/component";
import { LinkedLogo } from "~/components/logo/components/linked-logo/component";
import { SearchField } from "~/components/search-field/component";
import {
  DESKTOP_SEARCH_POPOVER_ID,
  SEARCH_PANEL_DESKTOP_CLASSES,
} from "~/components/search-panel/components/search-panel-desktop/constants";
import { useSearchPopover } from "~/components/search-panel/hooks/use-search-popover/hook";
import { ShoppingBagButton } from "~/components/shopping-bag-button/component";
import { ShoppingListButton } from "~/components/shopping-list-button/component";
import { UniversalComponent } from "~/components/universal-component/component";
import { BffComponentExtended } from "~/components/universal-component/types";
import { searchPanelConfig } from "~/constants/components-configs/search-panel-config";
import { THROTTLE_DELAY_100 } from "~/constants/delay-constants";
import { Routes } from "~/constants/request";
import {
  SearchDataContext,
  SearchVisibilityContext,
} from "~/context/search/context";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useIsShoppingBagEnabled } from "~/hooks/use-is-shopping-bag-enabled/hook";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { Close24 } from "~/theme/icons/components/close";
import { getFormatMedia } from "~/theme/utils";
import { Nullable } from "~/types/general.types";

import { SearchPanelDesktopContainer } from "./styled";

const DEFAULT_ICONS_COUNT = 0;
const INITIAL_PANEL_POSITION = 104;

export interface SearchPanelProps {
  searchPanel?: Nullable<SearchPanel>;
  wrapperClassName?: string;
  logo?: Nullable<ImageCommon>;
}

export const SearchPanelDesktop: React.FC<SearchPanelProps> = ({
  searchPanel,
  wrapperClassName,
  logo,
}) => {
  const theme = useTheme();
  const { BREAKPOINT_DESKTOP } = getFormatMedia(theme);
  const isDesktopView = useMediaQuery(BREAKPOINT_DESKTOP);
  const { locale } = useParams<GlobalPathParams>();
  const config = useAzureConfigurator(locale);

  const isMyAccountEnabled = useMemo(
    () => config?.featureFlags?.myAccount?.enabled,
    [config],
  );

  const isShoppingBagEnabled = useIsShoppingBagEnabled();
  const isShoppingListEnabled = useMemo(
    () => config?.featureFlags?.shoppingList?.enabled,
    [config],
  );

  const iconsCount = useMemo(() => {
    let result = DEFAULT_ICONS_COUNT;
    if (isMyAccountEnabled) {
      result += 1;
    }
    if (isShoppingListEnabled) {
      result += 1;
    }
    if (isShoppingBagEnabled) {
      result += 1;
    }
    return result;
  }, [isMyAccountEnabled, isShoppingListEnabled, isShoppingBagEnabled]);

  const {
    query,
    queryRevision,
    suggestions,
    handleQueryChange,
    handleQueryClear,
    handleSearchClick,
  } = React.useContext(SearchDataContext) ?? {};
  const { isSearchOpen, handleSearchClose, handleSearchOpen } =
    React.useContext(SearchVisibilityContext) ?? {};
  const isSuggestionsOpen = Boolean(suggestions?.length);

  const searchPanelRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  const [panelPosition, setPanelPosition] = useState<number>(INITIAL_PANEL_POSITION);
  const { handleToggle, popoverRef, supportsPopover } =
    useSearchPopover<HTMLDialogElement>({
      breakpoint: BREAKPOINT_DESKTOP,
      handleSearchClose,
      handleSearchOpen,
      isDesktopView,
      isSearchOpen,
      viewport: "desktop",
    });

  const throttledSetSearchPanelPosition = useThrottledCallback(() => {
    const headerPaddingTop = 8;
    const drawerMargin = 16;
    const searchPanelHeight =
      searchPanelRef?.current?.getBoundingClientRect().height || 0;
    const documentScrollPosition = document.documentElement.scrollTop;
    const panelPosition =
      searchPanelHeight + headerPaddingTop + drawerMargin - documentScrollPosition;
    setPanelPosition(panelPosition);
  }, THROTTLE_DELAY_100);

  useEffect(() => {
    throttledSetSearchPanelPosition();

    // eslint-disable-next-line no-restricted-globals
    window.addEventListener("scroll", throttledSetSearchPanelPosition);

    return () => {
      throttledSetSearchPanelPosition.cancel();
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener("scroll", throttledSetSearchPanelPosition);
    };
  });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (handleQueryChange) {
        handleQueryChange(event.currentTarget.value);
      }
    },
    [handleQueryChange],
  );

  const onCloseButtonClick = useCallback(() => {
    handleSearchClose?.();
  }, [handleSearchClose]);

  const isInlineSearchOpen = supportsPopover === false && isSearchOpen;

  useEffect(() => {
    if (isSearchOpen) {
      const searchField = searchPanelRef.current?.querySelector(
        "#search-field",
      ) as HTMLElement;

      triggerElementRef.current = searchField;

      const previousOverflow = document.body.style.overflow;
      const main = document.body.querySelector("main");
      const previousAriaHidden = main?.getAttribute("aria-hidden");
      document.body.style.overflow = "hidden";
      main?.setAttribute("aria-hidden", "true");

      return () => {
        document.body.style.overflow = previousOverflow;
        if (previousAriaHidden == null) {
          main?.removeAttribute("aria-hidden");
        } else {
          main?.setAttribute("aria-hidden", previousAriaHidden);
        }
      };
    } else if (triggerElementRef.current) {
      const searchField = searchPanelRef.current?.querySelector(
        '[data-testautomation-id="search-popover-trigger"], [data-testautomation-id="search-field"]',
      ) as HTMLElement;

      if (searchField) {
        searchField.focus();
      }

      triggerElementRef.current = null;
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      const frameId = requestAnimationFrame(() => {
        const panel =
          supportsPopover === false ? searchPanelRef.current : popoverRef.current;
        const searchInput = panel?.querySelector(
          '[data-testautomation-id="search-field-input"]:not(:disabled)',
        ) as HTMLInputElement;
        searchInput?.focus();
      });

      return () => cancelAnimationFrame(frameId);
    }
  }, [isSearchOpen, supportsPopover, popoverRef]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === "Escape") {
        handleSearchClose?.();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [handleSearchClose]);

  const topLine = useMemo(() => {
    let searchField: React.ReactNode;

    if (isInlineSearchOpen) {
      searchField = (
        <SearchField
          className={SEARCH_PANEL_DESKTOP_CLASSES.searchFieldCentered}
          fullWidth
          value={query}
          valueRevision={queryRevision}
          onChange={handleChange}
          onHydratedValue={handleQueryChange}
          isSearchOpen
          isSuggestionsOpen={isSuggestionsOpen}
          onClear={handleQueryClear}
          onSearch={handleSearchClick}
          onEscape={handleSearchClose}
          translations={searchPanel?.props?.translations}
        />
      );
    } else if (supportsPopover === false) {
      searchField = (
        <SearchField
          mode="button"
          className={SEARCH_PANEL_DESKTOP_CLASSES.searchFieldRight}
          onClick={handleSearchOpen}
          value={query}
          valueRevision={queryRevision}
          isSuggestionsOpen={isSuggestionsOpen}
          onChange={handleChange}
          onClear={handleQueryClear}
          onSearch={handleSearchClick}
          onEscape={handleSearchClose}
          translations={searchPanel?.props?.translations}
        />
      );
    } else {
      searchField = (
        <Box
          className={clsx(
            SEARCH_PANEL_DESKTOP_CLASSES.searchFieldRight,
            SEARCH_PANEL_DESKTOP_CLASSES.searchFieldTriggerWrapper,
          )}
        >
          <SearchField
            mode="button"
            value={query}
            valueRevision={queryRevision}
            tabIndex={-1}
            aria-hidden="true"
            translations={searchPanel?.props?.translations}
          />
          <button
            type="button"
            className={SEARCH_PANEL_DESKTOP_CLASSES.searchFieldTrigger}
            aria-label={searchPanel?.props?.translations?.searchText ?? "Search"}
            popoverTarget={DESKTOP_SEARCH_POPOVER_ID}
            popoverTargetAction="show"
            {...getTestAutomationProps("search-popover-trigger")}
          />
        </Box>
      );
    }

    return (
      <Box className={SEARCH_PANEL_DESKTOP_CLASSES.toolbar}>
        <LinkedLogo
          className={
            isInlineSearchOpen
              ? SEARCH_PANEL_DESKTOP_CLASSES.logoLeft
              : SEARCH_PANEL_DESKTOP_CLASSES.logoCentered
          }
          size="large"
          image={logo}
          href={Routes.HOME}
          onClick={handleSearchClose}
        />
        {searchField}
        {!isInlineSearchOpen && (
          <Box
            className={SEARCH_PANEL_DESKTOP_CLASSES.iconsWrapper}
            id="account-toolbar"
          >
            {isShoppingListEnabled && (
              <ShoppingListButton
                className={SEARCH_PANEL_DESKTOP_CLASSES.shoppingListButton}
              />
            )}
            {isMyAccountEnabled && (
              <AccountButtonDesktop
                className={SEARCH_PANEL_DESKTOP_CLASSES.accountButton}
              />
            )}
            {isShoppingBagEnabled && <ShoppingBagButton />}
          </Box>
        )}
        {isInlineSearchOpen && (
          <IconButton
            className={SEARCH_PANEL_DESKTOP_CLASSES.closeButton}
            color="inherit"
            aria-label="close search panel"
            onClick={onCloseButtonClick}
            {...getTestAutomationProps("close-button")}
            size="large"
          >
            <Close24 />
          </IconButton>
        )}
      </Box>
    );
  }, [
    logo,
    query,
    queryRevision,
    isSuggestionsOpen,
    isInlineSearchOpen,
    isMyAccountEnabled,
    isShoppingListEnabled,
    isShoppingBagEnabled,
    handleSearchClose,
    handleChange,
    handleQueryChange,
    handleQueryClear,
    supportsPopover,
    handleSearchOpen,
    handleSearchClick,
    onCloseButtonClick,
    searchPanel?.props?.translations,
  ]);

  const drawerContent = (
    <>
      <div className={SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}>
        {isSearchOpen && searchPanel && (
          <UniversalComponent
            meta={searchPanel._meta}
            props={searchPanel.props}
            component={searchPanel.component ? BffComponentType.SEARCH_PANEL : null}
            elements={searchPanel.children as BffComponentExtended[]}
            componentsConfigs={[searchPanelConfig]}
          />
        )}
      </div>
      <div
        className={SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}
        onClick={handleSearchClose}
        aria-hidden="true"
      />
    </>
  );

  return (
    <FocusTrap open={isSearchOpen ?? false} disableEnforceFocus>
      <SearchPanelDesktopContainer
        isSearchOpen={isInlineSearchOpen}
        iconsCount={iconsCount}
        panelPosition={panelPosition}
      >
        <Box
          className={clsx(
            SEARCH_PANEL_DESKTOP_CLASSES.toolbarWrapper,
            wrapperClassName,
          )}
          {...getTestAutomationProps("search-panel")}
          {...{ ref: searchPanelRef }}
          id="search-panel"
        >
          {topLine}
        </Box>
        {supportsPopover === false ? (
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal={isSearchOpen ? "true" : undefined}
            aria-label="Search panel"
            aria-hidden={!isSearchOpen}
            inert={!isSearchOpen || undefined}
            className={clsx(
              SEARCH_PANEL_DESKTOP_CLASSES.drawer,
              isSearchOpen && SEARCH_PANEL_DESKTOP_CLASSES.drawerVisible,
            )}
          >
            {drawerContent}
          </div>
        ) : (
          <dialog
            ref={popoverRef}
            id={DESKTOP_SEARCH_POPOVER_ID}
            popover="auto"
            aria-label="Search panel"
            className={clsx(
              SEARCH_PANEL_DESKTOP_CLASSES.drawer,
              SEARCH_PANEL_DESKTOP_CLASSES.nativePopover,
            )}
            onToggle={handleToggle}
          >
            <div className={SEARCH_PANEL_DESKTOP_CLASSES.popoverHeader}>
              <Box
                className={clsx(
                  SEARCH_PANEL_DESKTOP_CLASSES.popoverToolbarWrapper,
                  wrapperClassName,
                )}
              >
                <Box className={SEARCH_PANEL_DESKTOP_CLASSES.toolbar} role="search">
                  <LinkedLogo
                    className={SEARCH_PANEL_DESKTOP_CLASSES.logoLeft}
                    size="large"
                    image={logo}
                    href={Routes.HOME}
                    onClick={handleSearchClose}
                  />
                  <SearchField
                    id="desktop-popover-search-input"
                    className={SEARCH_PANEL_DESKTOP_CLASSES.searchFieldCentered}
                    fullWidth
                    value={query}
                    valueRevision={queryRevision}
                    onChange={handleChange}
                    onHydratedValue={handleQueryChange}
                    isSearchOpen
                    isSuggestionsOpen={isSuggestionsOpen}
                    onClear={handleQueryClear}
                    onSearch={handleSearchClick}
                    onEscape={handleSearchClose}
                    translations={searchPanel?.props?.translations}
                  />
                  <IconButton
                    className={SEARCH_PANEL_DESKTOP_CLASSES.closeButton}
                    color="inherit"
                    aria-label="close search panel"
                    onClick={onCloseButtonClick}
                    popoverTarget={DESKTOP_SEARCH_POPOVER_ID}
                    popoverTargetAction="hide"
                    {...getTestAutomationProps("close-button")}
                    size="large"
                  >
                    <Close24 />
                  </IconButton>
                </Box>
              </Box>
            </div>
            {drawerContent}
          </dialog>
        )}
      </SearchPanelDesktopContainer>
    </FocusTrap>
  );
};
